import React, { useState } from "react";
import {
  Bot,
  Sparkles,
  Puzzle,
  Rocket,
  Package,
  Zap,
  Users,
  GitBranch,
  ArrowRight,
  CheckCircle,
  Clock,
  Target,
  Lightbulb,
  Shield,
  Layers,
  TrendingUp,
  ChevronRight,
  Star,
  Award,
  Code,
  FileText,
  Mail,
  Calendar,
} from "lucide-react";

const UsageAgentElva = () => {
  const [activeCategory, setActiveCategory] = useState("productivity");

  const CategoryButton = ({
    id,
    label,
    icon: Icon,
    count,
    active,
    onClick,
  }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${
        active
          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
          : "bg-white text-gray-700 hover:shadow-md border border-gray-200"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
      <span
        className={`text-xs px-2 py-1 rounded-full ${
          active ? "bg-white/20" : "bg-gray-100"
        }`}
      >
        {count}
      </span>
    </button>
  );

  const AgentCard = ({
    name,
    description,
    status,
    icon: Icon,
    color,
    tags,
  }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center`}
        >
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <span
          className={`text-xs px-3 py-1 rounded-full ${
            status === "active"
              ? "bg-green-100 text-green-700"
              : status === "beta"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {status === "active" ? "활성" : status === "beta" ? "베타" : "예정"}
        </span>
      </div>
      <h3 className="font-semibold text-gray-800 mb-2">{name}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );

  const TimelineItem = ({ phase, title, description, icon: Icon, status }) => (
    <div
      className={`relative flex items-start gap-4 ${
        status === "completed" ? "opacity-100" : "opacity-60"
      }`}
    >
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          status === "completed"
            ? "bg-green-100"
            : status === "current"
            ? "bg-blue-100"
            : "bg-gray-100"
        }`}
      >
        <Icon
          className={`w-6 h-6 ${
            status === "completed"
              ? "text-green-600"
              : status === "current"
              ? "text-blue-600"
              : "text-gray-400"
          }`}
        />
      </div>
      <div className="flex-1 pb-8">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-semibold text-gray-800">{title}</h4>
          <span className="text-xs text-gray-500">{phase}</span>
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-400/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/20 rounded-full filter blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="relative container mx-auto px-6">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-blue-700 text-sm font-medium">
              <Bot className="w-4 h-4" />
              AI Agent Library
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Agent Elva
              </span>
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              필요한 기능을 필요한 만큼만. Elva의 AI 에이전트 라이브러리는
              다양한 단발성 기능을 제공하여 사용자 피로감 없이 효율적인 작업을
              돕습니다.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              Agent Elva의 핵심 가치
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto">
                  <Zap className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  일회성 실행
                </h3>
                <p className="text-gray-600">
                  복잡한 설정 없이 필요한 기능을 즉시 사용할 수 있습니다
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto">
                  <Puzzle className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  모듈화된 기능
                </h3>
                <p className="text-gray-600">
                  각 에이전트는 독립적으로 작동하며 필요에 따라 조합 가능합니다
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto">
                  <Users className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  사용자 중심
                </h3>
                <p className="text-gray-600">
                  피로감 없는 UX를 위해 핵심 기능에만 집중합니다
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agent Categories */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              에이전트 카테고리
            </h2>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <CategoryButton
                id="productivity"
                label="생산성"
                icon={Target}
                active={activeCategory === "productivity"}
                onClick={() => setActiveCategory("productivity")}
              />
              <CategoryButton
                id="analysis"
                label="분석"
                icon={TrendingUp}
                active={activeCategory === "analysis"}
                onClick={() => setActiveCategory("analysis")}
              />
              <CategoryButton
                id="creative"
                label="크리에이티브"
                icon={Lightbulb}
                active={activeCategory === "creative"}
                onClick={() => setActiveCategory("creative")}
              />
              <CategoryButton
                id="utility"
                label="유틸리티"
                icon={Package}
                active={activeCategory === "utility"}
                onClick={() => setActiveCategory("utility")}
              />
            </div>

            {/* Agent Grid */}
            {/* <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeCategory === "productivity" && (
                <>
                  <AgentCard
                    name="Quick Summary"
                    description="긴 문서를 빠르게 요약하여 핵심 내용을 파악합니다"
                    status="active"
                    icon={FileText}
                    color="blue"
                    tags={["텍스트", "요약", "5분"]}
                  />
                  <AgentCard
                    name="Task Prioritizer"
                    description="할 일 목록을 중요도와 긴급도에 따라 자동 정렬합니다"
                    status="active"
                    icon={CheckCircle}
                    color="green"
                    tags={["생산성", "관리", "자동화"]}
                  />
                  <AgentCard
                    name="Meeting Notes"
                    description="회의 내용을 실시간으로 정리하고 액션 아이템을 추출합니다"
                    status="beta"
                    icon={Users}
                    color="purple"
                    tags={["회의", "노트", "협업"]}
                  />
                  <AgentCard
                    name="Email Composer"
                    description="상황에 맞는 전문적인 이메일을 자동으로 작성합니다"
                    status="active"
                    icon={Mail}
                    color="indigo"
                    tags={["이메일", "작성", "비즈니스"]}
                  />
                  <AgentCard
                    name="Code Reviewer"
                    description="코드 품질을 분석하고 개선 사항을 제안합니다"
                    status="beta"
                    icon={Code}
                    color="yellow"
                    tags={["개발", "코드", "리뷰"]}
                  />
                  <AgentCard
                    name="Schedule Optimizer"
                    description="일정을 분석하여 최적의 스케줄을 제안합니다"
                    status="coming"
                    icon={Calendar}
                    color="gray"
                    tags={["일정", "최적화", "계획"]}
                  />
                </>
              )}
            </div> */}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              Agent 아키텍처
            </h2>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <Layers className="w-6 h-6 text-blue-600" />
                    모듈형 설계
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">
                        독립 실행
                      </h4>
                      <p className="text-sm text-gray-600">
                        각 에이전트는 독립적으로 실행되어 서로 영향을 주지
                        않습니다
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">
                        선택적 조합
                      </h4>
                      <p className="text-sm text-gray-600">
                        필요에 따라 여러 에이전트를 조합하여 사용할 수 있습니다
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">경량화</h4>
                      <p className="text-sm text-gray-600">
                        각 기능은 최소한의 리소스만을 사용하도록
                        최적화되었습니다
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <Shield className="w-6 h-6 text-purple-600" />
                    보안 & 프라이버시
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">
                        데이터 격리
                      </h4>
                      <p className="text-sm text-gray-600">
                        각 에이전트의 데이터는 완전히 격리되어 처리됩니다
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">
                        임시 처리
                      </h4>
                      <p className="text-sm text-gray-600">
                        일회성 실행 후 모든 데이터는 자동으로 삭제됩니다
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-medium text-gray-800 mb-2">
                        권한 관리
                      </h4>
                      <p className="text-sm text-gray-600">
                        최소 권한 원칙에 따라 필요한 권한만 요청합니다
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development Roadmap */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              개발 로드맵
            </h2>

            <div className="relative">
              <div className="absolute left-6 top-6 bottom-0 w-0.5 bg-gray-300"></div>

              <div className="space-y-0">
                <TimelineItem
                  phase="Phase 1"
                  title="기본 에이전트 출시"
                  description="생산성, 분석, 크리에이티브 분야의 핵심 에이전트 30개 출시"
                  icon={Rocket}
                  status="completed"
                />
                <TimelineItem
                  phase="Phase 2"
                  title="에이전트 간 연동"
                  description="서로 다른 에이전트를 연결하여 복합 작업 수행 가능"
                  icon={GitBranch}
                  status="current"
                />
                <TimelineItem
                  phase="Phase 3"
                  title="커스텀 에이전트"
                  description="손쉽게 기업별 맞춤형 에이전트 제작 가능"
                  icon={Code}
                  status="planned"
                />
                <TimelineItem
                  phase="Phase 4"
                  title="AI 오케스트레이션"
                  description="복잡한 워크플로우를 자동으로 구성하고 실행"
                  icon={Sparkles}
                  status="planned"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              왜 Agent Elva인가?
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Award className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  사용자 경험 최우선
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">
                      피로감 없는 간결한 인터페이스
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">
                      필요한 기능만 선택적으로 사용
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">복잡한 설정 과정 없음</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Star className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  지속적인 혁신
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">
                      사용자 피드백 기반 개선
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">
                      새로운 에이전트 정기 출시
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">
                      최신 AI 기술 즉시 적용
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            필요한 순간, 필요한 AI 에이전트
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Agent Elva와 함께 복잡함은 줄이고 생산성은 높이세요
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors shadow-lg inline-flex items-center gap-2">
              <Bot className="w-5 h-5" />
              에이전트 둘러보기
            </button>
            <button className="bg-transparent text-white border-2 border-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <ArrowRight className="w-5 h-5" />
              사용 가이드
            </button>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default UsageAgentElva;
